import React, { useEffect } from 'react'
import { Field, FieldArray, FieldProps, useFormikContext } from 'formik'
import { Input } from 'reactstrap'
import env from 'environment_system/env_system'
import ErrorTooltip from 'Components/ErrorTooltip'
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider'
import { isFormUnchanged } from 'GenericForms/Helper'

interface FourHrTableComponentType {
    vesselMachinery: any,
    tableHeader: string,
    valueName: string,
    valueSubName: any[],
    initialSubName: any[],
    showMachineryName: boolean,
    energyConsumedField: string,
    applicableTimePeriod: any,
    editValues?: any,
    setEditValues?: any,
    revPopup?: boolean,
    spOpConstant?: number,
}

const FourHrTableComponent = ({
    vesselMachinery,
    tableHeader,
    valueName,
    valueSubName,
    initialSubName,
    showMachineryName,
    energyConsumedField,
    editValues,
    setEditValues,
    revPopup = true,
    spOpConstant,
}: FourHrTableComponentType) => {
    /** State variables */
    const { values, errors, touched, handleChange, handleBlur }:
        { values: any, errors: any, touched: any, handleChange: any, handleBlur: any } = useFormikContext();
    const { discardwarning } = useEditReport();
    const valueNameSplit = valueName.split('.');
    /** State variables end */

    /** Calculate W */
    const calculateW = (form: any, values: any, name: any, e: any, index: number) => {
        if (!Number.isNaN(e)) {
            if (name === 'hours_of_consumption') {
                form.setFieldValue(`${valueName}.${index}.w`,
                    (1.73205080757 *
                        e *
                        valueSubName[index]?.v *
                        valueSubName[index]?.i *
                        valueSubName[index]?.pf * 0.001).toFixed(2));
            } else if (name === 'v') {
                form.setFieldValue(`${valueName}.${index}.w`,
                    (1.73205080757 *
                        valueSubName[index]?.hours_of_consumption *
                        e *
                        valueSubName[index]?.i *
                        valueSubName[index]?.pf * 0.001).toFixed(2));
            } else if (name === 'i') {
                form.setFieldValue(`${valueName}.${index}.w`,
                    (1.73205080757 *
                        valueSubName[index]?.hours_of_consumption *
                        valueSubName[index]?.v *
                        e *
                        valueSubName[index]?.pf * 0.001).toFixed(2));
            } else if (name === 'pf') {
                form.setFieldValue(`${valueName}.${index}.w`,
                    (1.73205080757 *
                        valueSubName[index]?.hours_of_consumption *
                        valueSubName[index]?.v *
                        valueSubName[index]?.i *
                        e * 0.001).toFixed(2));
            }
        }
    }

    /** Calculate sum of w */
    const loadwsum = (values: any[], field: string, form: any) => {
        let sum = 0;
        values.map((val: any) => {
            sum += parseFloat(val?.w ?? 0);
            return '';
        })
        form.setFieldValue(field, sum.toFixed(2));
    }

    /** useEffect */
    useEffect(() => {
        if (editValues && Array.isArray(editValues?.chief_engineer?.special_operation_4_hours_reporting) &&
            !isFormUnchanged(initialSubName, valueSubName)) {
            setEditValues({
                ...editValues,
                chief_engineer: {
                    ...editValues?.chief_engineer,
                    special_operation_4_hours_reporting: [
                        ...editValues?.chief_engineer?.special_operation_4_hours_reporting?.filter((item: any) => item.special_operation !== spOpConstant),
                        ...valueSubName?.filter((item: any) => item.special_operation === spOpConstant)
                    ]
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueSubName])
    /** useEffect end */

    /** Function to destructure error tooltip message */
    const showErrorTooltipMessage = (time: any, field: string) => {
        if (errors && touched && env?.form_validation === true && revPopup &&
            document.getElementById(`${field}_${valueSubName.indexOf(time)}_${valueNameSplit[0]}`) !== null
        ) {
            if (valueNameSplit?.length > 1) {
                if (errors[valueNameSplit?.[0]]?.[valueNameSplit?.[1]]?.[valueSubName?.indexOf(time)]?.[field] &&
                    touched[valueNameSplit?.[0]]?.[valueNameSplit?.[1]]?.[valueSubName?.indexOf(time)]?.[field]) {
                    return errors[valueNameSplit?.[0]]?.[valueNameSplit?.[1]]?.[valueSubName?.indexOf(time)]?.[field];
                }
            } else {
                if (errors[valueNameSplit?.[0]] && errors[valueNameSplit?.[0]]?.[valueSubName.indexOf(time)]?.[field] &&
                    touched[valueNameSplit?.[0]] && touched[valueNameSplit?.[0]]?.[valueSubName.indexOf(time)]?.[field]) {
                    return errors[valueNameSplit?.[0]]?.[valueSubName?.indexOf(time)]?.[field];
                }
            }
        }
    }

    return (
        <React.Fragment>
            {showMachineryName === true && valueSubName && valueSubName !== undefined && valueSubName?.some((item: any) =>
                item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group &&
                item.special_operation === spOpConstant
            ) &&
                <h6 className='mb-3 mt-3 ps-3'>{vesselMachinery.vessel_machinery_ec_group_name}</h6>
            }
            <div className="table-responsive p-0 ">
                {valueSubName?.some((item: any) =>
                    item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group) &&
                    <table className="table mb-0">
                        <thead className="table-light">
                            <tr>
                                <th className="text-center">Period</th>
                                <th className="asteric text-center">{tableHeader}</th>
                                <th className="asteric text-center">V</th>
                                <th className="asteric text-center">I</th>
                                <th className="asteric text-center">Pf</th>
                                <th className="asteric text-center">W=kWh</th>
                            </tr>
                        </thead>
                        <FieldArray name={`${valueName}`}>
                            {() => (
                                <React.Fragment>
                                    {valueSubName && valueSubName !== undefined && valueSubName?.filter((item: any) =>
                                        item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group)
                                        ?.sort((a: any, b: any) => a.period - b.period)?.map((time: any, index: number) => {
                                            return (
                                                <tbody key={index}>
                                                    <tr>
                                                        <td className='align-middle p-2 text-center'>{time.time_period}</td>
                                                        <td className='text-center'>
                                                            <div className="d-inline-block">
                                                                <div className="input-group">
                                                                    <Field name={`${valueName}.${valueSubName.indexOf(time)}.hours_of_consumption`}>
                                                                        {({ field, form }: FieldProps) => (
                                                                            <Input
                                                                                onFocus={(e) => e.target.select()}
                                                                                type="text"
                                                                                className="form-control text-right max-width-7"
                                                                                id={`hours_of_consumption_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                                name={field.name}
                                                                                onChange={(e: any) => {
                                                                                    handleChange(e);
                                                                                    handleBlur(e);
                                                                                    calculateW(
                                                                                        form,
                                                                                        values,
                                                                                        'hours_of_consumption',
                                                                                        parseFloat(e.target.value),
                                                                                        valueSubName.indexOf(time)
                                                                                    );
                                                                                    loadwsum(
                                                                                        valueSubName?.filter((item: any) =>
                                                                                            item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                        energyConsumedField,
                                                                                        form
                                                                                    )
                                                                                }}
                                                                                onBlur={(e: any) => {
                                                                                    handleChange(e);
                                                                                    handleBlur(e);
                                                                                    calculateW(
                                                                                        form,
                                                                                        values,
                                                                                        'hours_of_consumption',
                                                                                        parseFloat(e.target.value),
                                                                                        valueSubName.indexOf(time)
                                                                                    );
                                                                                    loadwsum(
                                                                                        valueSubName?.filter((item: any) =>
                                                                                            item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                        energyConsumedField,
                                                                                        form
                                                                                    )
                                                                                }}
                                                                                autoComplete="off"
                                                                                defaultValue={valueSubName?.[valueSubName.indexOf(time)]?.hours_of_consumption}
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                    <div className="input-group-text">hr</div>
                                                                </div>
                                                            </div>
                                                            {showErrorTooltipMessage(time, 'hours_of_consumption') && !discardwarning &&
                                                                <ErrorTooltip
                                                                    target={`hours_of_consumption_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                    message={showErrorTooltipMessage(time, 'hours_of_consumption')}
                                                                    open={showErrorTooltipMessage(time, 'hours_of_consumption') ? true : false}
                                                                    key={`${valueSubName?.indexOf(time)}_${revPopup}`}
                                                                />
                                                            }
                                                        </td>
                                                        <td className='text-center'>
                                                            <div className="d-inline-block">
                                                                <Field name={`${valueName}.${valueSubName.indexOf(time)}.v`}>
                                                                    {({ field, form }: FieldProps) => (
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type="text"
                                                                            className="form-control text-right max-width-7"
                                                                            id={`v_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                            name={field.name}
                                                                            onChange={(e: any) => {
                                                                                handleChange(e);
                                                                                handleBlur(e);
                                                                                calculateW(
                                                                                    form,
                                                                                    values,
                                                                                    'v',
                                                                                    parseFloat(e.target.value),
                                                                                    valueSubName.indexOf(time)
                                                                                );
                                                                                loadwsum(
                                                                                    valueSubName?.filter((item: any) =>
                                                                                        item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                    energyConsumedField,
                                                                                    form
                                                                                )
                                                                            }}
                                                                            onBlur={(e: any) => {
                                                                                handleChange(e);
                                                                                handleBlur(e);
                                                                                calculateW(
                                                                                    form,
                                                                                    values,
                                                                                    'v',
                                                                                    parseFloat(e.target.value),
                                                                                    valueSubName.indexOf(time)
                                                                                );
                                                                                loadwsum(
                                                                                    valueSubName?.filter((item: any) =>
                                                                                        item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                    energyConsumedField,
                                                                                    form
                                                                                )
                                                                            }}
                                                                            autoComplete="off"
                                                                            defaultValue={valueSubName[valueSubName.indexOf(time)].v}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                            {showErrorTooltipMessage(time, 'v') && !discardwarning &&
                                                                <ErrorTooltip
                                                                    target={`v_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                    message={showErrorTooltipMessage(time, 'v')}
                                                                    open={showErrorTooltipMessage(time, 'v') ? true : false}
                                                                    key={`${valueSubName?.indexOf(time)}_${revPopup}`}
                                                                />
                                                            }
                                                        </td>
                                                        <td className='text-center'>
                                                            <div className="d-inline-block">
                                                                <Field name={`${valueName}.${valueSubName.indexOf(time)}.i`}>
                                                                    {({ field, form }: FieldProps) => (
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type="text"
                                                                            className="form-control text-right max-width-7"
                                                                            id={`i_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                            name={field.name}
                                                                            onChange={(e: any) => {
                                                                                handleChange(e);
                                                                                handleBlur(e);
                                                                                calculateW(
                                                                                    form,
                                                                                    values,
                                                                                    'i',
                                                                                    parseFloat(e.target.value),
                                                                                    valueSubName.indexOf(time)
                                                                                );
                                                                                loadwsum(
                                                                                    valueSubName?.filter((item: any) =>
                                                                                        item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                    energyConsumedField,
                                                                                    form
                                                                                )
                                                                            }}
                                                                            onBlur={(e: any) => {
                                                                                handleChange(e);
                                                                                handleBlur(e);
                                                                                calculateW(
                                                                                    form,
                                                                                    values,
                                                                                    'i',
                                                                                    parseFloat(e.target.value),
                                                                                    valueSubName.indexOf(time)
                                                                                );
                                                                                loadwsum(
                                                                                    valueSubName?.filter((item: any) =>
                                                                                        item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                    energyConsumedField,
                                                                                    form
                                                                                )
                                                                            }}
                                                                            autoComplete="off"
                                                                            defaultValue={valueSubName[valueSubName.indexOf(time)].i}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                            {showErrorTooltipMessage(time, 'i') && !discardwarning &&
                                                                <ErrorTooltip
                                                                    target={`i_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                    message={showErrorTooltipMessage(time, 'i')}
                                                                    open={showErrorTooltipMessage(time, 'i') ? true : false}
                                                                    key={`${valueSubName?.indexOf(time)}_${revPopup}`}
                                                                />
                                                            }
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-inline-block">
                                                                <Field name={`${valueName}.${valueSubName.indexOf(time)}.pf`}>
                                                                    {({ field, form }: FieldProps) => (
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type="text"
                                                                            className="form-control text-right max-width-7"
                                                                            id={`pf_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                            name={field.name}
                                                                            onChange={(e: any) => {
                                                                                handleChange(e);
                                                                                handleBlur(e);
                                                                                calculateW(
                                                                                    form,
                                                                                    values,
                                                                                    'pf',
                                                                                    parseFloat(e.target.value),
                                                                                    valueSubName.indexOf(time)
                                                                                );
                                                                                loadwsum(
                                                                                    valueSubName?.filter((item: any) =>
                                                                                        item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                    energyConsumedField,
                                                                                    form
                                                                                )
                                                                            }}
                                                                            onBlur={(e: any) => {
                                                                                handleChange(e);
                                                                                handleBlur(e);
                                                                                calculateW(
                                                                                    form,
                                                                                    values,
                                                                                    'pf',
                                                                                    parseFloat(e.target.value),
                                                                                    valueSubName.indexOf(time)
                                                                                );
                                                                                loadwsum(
                                                                                    valueSubName?.filter((item: any) =>
                                                                                        item.vessel_machinery_ec_group === vesselMachinery?.vessel_machinery_ec_group),
                                                                                    energyConsumedField,
                                                                                    form
                                                                                )
                                                                            }}
                                                                            autoComplete="off"
                                                                            defaultValue={valueSubName[valueSubName.indexOf(time)].pf}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                            {showErrorTooltipMessage(time, 'pf') && !discardwarning &&
                                                                <ErrorTooltip
                                                                    target={`pf_${valueSubName?.indexOf(time)}_${valueNameSplit[0]}`}
                                                                    message={showErrorTooltipMessage(time, 'pf')}
                                                                    open={showErrorTooltipMessage(time, 'pf') ? true : false}
                                                                    key={`${valueSubName?.indexOf(time)}_${revPopup}`}
                                                                />
                                                            }
                                                        </td>
                                                        <td className='text-center'>
                                                            <div className="d-inline-block">
                                                                <Field
                                                                    type="text"
                                                                    className="form-control text-right max-width-7"
                                                                    id={time.id}
                                                                    name={`${valueName}.${valueSubName.indexOf(time)}.w`}
                                                                    disabled
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            )
                                        })}
                                </React.Fragment>
                            )}
                        </FieldArray>
                    </table>
                }
            </div>
        </React.Fragment>
    )
}

export default FourHrTableComponent
